import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  CircularProgress,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

interface RefreshModalProps {
  open: boolean;
  onClose: () => void;
  error?: string | null;
  isRefreshing?: boolean;
}

const RefreshModal: React.FC<RefreshModalProps> = ({
  open,
  onClose,
  error,
  isRefreshing = false,
}) => {
  const [showCloseButton, setShowCloseButton] = useState(false);

  useEffect(() => {
    if (open) {
      // Show close button after 3 seconds minimum
      const timer = setTimeout(() => {
        setShowCloseButton(true);
      }, 3000);

      return () => clearTimeout(timer);
    } else {
      setShowCloseButton(false);
    }
  }, [open]);

  // Auto-close modal after minimum time and when refresh is complete
  useEffect(() => {
    if (open && showCloseButton && !isRefreshing && !error) {
      // Close automatically if refresh is complete and no error
      const closeTimer = setTimeout(() => {
        onClose();
      }, 500); // Small delay to show completion state

      return () => clearTimeout(closeTimer);
    }
  }, [open, showCloseButton, isRefreshing, error, onClose]);

  const handleClose = (): void => {
    if (showCloseButton) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={!showCloseButton}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pb: 1,
        }}
      >
        <RefreshIcon color="primary" />
        <Typography variant="h6" component="span">
          Refreshing Notifications
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <CircularProgress size={48} sx={{ mb: 2 }} />
          )}

          <Typography variant="body1" sx={{ mb: 2 }}>
            Manually refreshing notification data...
          </Typography>

          <Alert severity="info" sx={{ textAlign: 'left' }}>
            <Typography variant="body2">
              <strong>Help:</strong> Data automatically refreshes every 15
              seconds without the need to press this button. You can manage your
              notification preferences in your User Settings.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default RefreshModal;
